import { warn } from '@execonline-inc/logging';
import { fromBool, toTask } from '@execonline-inc/maybe-adapter';
import { observer } from 'mobx-react';
import * as React from 'react';
import ChatReplyStore from '../../../ChatReplyStore';
import { error } from '../../../ErrorHandling';
import { TPlainTextKey } from '../../../Translations';
import Button from '../../Form/Button';
import ChatEditor from './StyleControls/ChatEditor';
import ChatEditorProvider from './StyleControls/ChatEditorProvider';
import BlockStyleControls from './StyleControls/BlockStyleControls';
import InlineStyleControls from './StyleControls/InlineStyleControls';
import * as style from './style.module.css';

interface Props {
  store: ChatReplyStore;
  placeholder: TPlainTextKey;
}

const TeamChatReplyEditor: React.FC<Props> = ({ store, placeholder }) => {
  const submitChatMessage = () =>
    toTask(error('Message not available'), store.message)
      .map((m) => fromBool(m.text.length > 0, m))
      .andThen(toTask(error('Message not available')))
      .fork((err) => warn(`Could not submit chat message: ${err.message}`), store.updating);

  const submit = (event: React.FormEvent<HTMLButtonElement>) => {
    event.preventDefault();
    submitChatMessage();
  };

  const disableSubmitButton = (): boolean => {
    switch (store.state.kind) {
      case 'updating':
        return true;
      case 'ready':
      case 'broadcasting':
      case 'hidden':
      case 'error':
      default:
        return false;
    }
  };

  return (
    <div className={style.replyField}>
      <div className={style.formWrapper}>
        <ChatEditorProvider>
          <form className={style.form}>
            <div className={style.textarea}>
              <ChatEditor
                store={store}
                submitChatMessage={submitChatMessage}
                placeholder={placeholder}
                contentClassName={style.editorContent}
                placeholderClassName={style.editorPlaceholder}
              />
            </div>
            <div className={style.editorControls}>
              <InlineStyleControls />
              <BlockStyleControls />
              <Button
                type="submit"
                className={style.submitButton}
                onClick={submit}
                disabled={disableSubmitButton()}
                value="Send"
              />
            </div>
          </form>
        </ChatEditorProvider>
      </div>
    </div>
  );
};
export default observer(TeamChatReplyEditor);
